import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMFacultyList from "../../features/university-manager/UMFacultyList";
import UMFacultyForm from "../../features/university-manager/UMFacultyForm";
import AppButton from "../../components/ui/AppButton";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";
import { useNotification } from "../../context/NotificationContext";
import useDebounce from "../../utils/useDebounce";

const UMFaculties = () => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();
  const { userRole, currentTenantId } = useTenant();
  const { showSuccess, showError } = useNotification();
  const [faculties, setFaculties] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [campusFilter, setCampusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [statusFilter, setStatusFilter] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const cache = useRef({});
  const invalidateCache = () => { cache.current = {}; };

  const fetchFaculties = useCallback(async () => {
    const key = `${statusFilter}|${campusFilter}|${currentPage}|${pageSize}|${debouncedSearchTerm}`;

    if (cache.current[key]) {
      const cached = cache.current[key];
      setFaculties(cached.content);
      setTotalPages(cached.totalPages);
      setTotalElements(cached.totalElements);
      return;
    }

    setLoading(true);
    const res = await umService.getFaculties({ userRole, currentTenantId, campusId: campusFilter || undefined, page: currentPage, size: pageSize, search: debouncedSearchTerm, status: statusFilter });
    if (res.success) {
      const data = { content: res.data.content, totalPages: res.data.totalPages, totalElements: res.data.totalElements };
      cache.current[key] = data;
      setFaculties(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    }
    setLoading(false);
  }, [userRole, currentTenantId, campusFilter, currentPage, pageSize, debouncedSearchTerm, statusFilter]);

  const fetchCampuses = useCallback(async () => {
    if (!can(PERMISSIONS.campus.list)) return;
    const res = await umService.getAllCampuses({ userRole, currentTenantId });
    if (res.success) setCampuses(res.data);
  }, [userRole, currentTenantId, can]);

  useEffect(() => { fetchFaculties(); }, [fetchFaculties]);
  useEffect(() => { fetchCampuses(); }, [fetchCampuses]);

  const handleCreate = () => {
    setEditingFaculty(null);
    setShowForm(true);
  };

  const handleEdit = (faculty) => {
    setEditingFaculty(faculty);
    setShowForm(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const res = editingFaculty
        ? await umService.updateFaculty({ id: editingFaculty.id, userRole, currentTenantId, data })
        : await umService.createFaculty({ userRole, currentTenantId, data });

      if (res.success) {
        showSuccess(t("crud.messages.saved"));
        setShowForm(false);
        setEditingFaculty(null);
        invalidateCache();
        fetchFaculties();
      } else {
        showError(t("crud.messages.saveFailed"));
      }
    } catch {
      // API error notification already shown by the interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faculty) => {
    if (!window.confirm(t("crud.messages.confirmDelete"))) return;

    const res = await umService.deleteFaculty({ id: faculty.id, userRole, currentTenantId });
    if (res.success) {
      showSuccess(t("crud.messages.deleted"));
      invalidateCache();
      fetchFaculties();
      return;
    }

    if (res.message === "FACULTY_HAS_DEPENDENCIES") {
      showError(t("crud.messages.facultyHasDependencies"));
      return;
    }

    showError(t("crud.messages.deleteFailed"));
  };

  const handleReactivate = async (faculty) => {
    if (!window.confirm(t("crud.messages.confirmReactivate"))) return;
    const res = await umService.updateFaculty({
      id: faculty.id,
      userRole,
      currentTenantId,
      data: { campusId: faculty.campusId, nameEn: faculty.nameEn, nameLocal: faculty.nameLocal, status: true },
    });
    if (res.success) {
      showSuccess(t("crud.messages.reactivated"));
      invalidateCache();
      fetchFaculties();
    } else {
      showError(t("crud.messages.reactivateFailed"));
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<AppButton key={i} variant={i === currentPage ? "primary" : "outline"} className="btn-sm mx-1" onClick={() => setCurrentPage(i)}>{i + 1}</AppButton>);
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">{tCommon("pagination.showingEntries", { start: currentPage * pageSize + 1, end: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements })}</div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>{tCommon("buttons.previous")}</AppButton>
          {pages}
          <AppButton variant="outline" className="btn-sm ms-2" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>{tCommon("buttons.next")}</AppButton>
        </div>
      </div>
    );
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.faculty.list)}>
      <div className="faculties-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("faculties.title")}</h1>
            <p className="text-muted mb-0">{t("faculties.subtitle")}</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0">{t("faculties.show")}:</label>
            <select className="form-select form-select-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }} style={{ width: "auto" }}>
              <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
            </select>
          </div>
        </div>
        <UMFacultyList
          faculties={faculties}
          loading={loading}
          totalElements={totalElements}
          campuses={campuses}
          campusFilter={campusFilter}
          onCampusFilterChange={(v) => { setCampusFilter(v); setCurrentPage(0); }}
          searchTerm={searchTerm}
          onSearchChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
          onClearFilters={() => { setSearchTerm(""); setCampusFilter(""); setCurrentPage(0); }}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onReactivate={handleReactivate}
          statusFilter={statusFilter}
          onStatusFilterChange={(val) => { setStatusFilter(val); setCurrentPage(0); }}
        />
        {renderPagination()}
      </div>
      </PermissionGuard>

      {showForm && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingFaculty ? t("crud.editFaculty") : t("crud.addFaculty")}
                </h5>
                <button className="btn-close" onClick={() => setShowForm(false)} disabled={saving}></button>
              </div>
              <div className="modal-body">
                <UMFacultyForm
                  initialData={editingFaculty}
                  campuses={campuses}
                  onSubmit={handleSave}
                  onCancel={() => setShowForm(false)}
                  loading={saving}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </UniversityManagerLayout>
  );
};

export default UMFaculties;
